import {useEffect, useLayoutEffect, useState} from 'react'
import {Outlet, useLocation} from 'react-router-dom'
import {useLayout} from './core'
import {HeaderWrapper} from './components/header'
import {ScrollTop} from './components/scroll-top'
import {Content} from './components/content'
import {FooterWrapper} from './components/footer'
import {Sidebar} from './components/sidebar'
import {DrawerMessenger, InviteUsers, UpgradePlan} from '../partials'
import {PageDataProvider} from './core'
import {reInitMenu} from '../helpers'
import {ToolbarWrapper} from './components/toolbar'
import {DrawerTicketDetail} from '../partials/layout/drawer-messenger/DrawerTicketDetail'
import {SideModalIPList} from '../partials/widgets/_new/modal/SideModalIPList'
import {DrawerUserDetails} from '../partials/widgets/_new/drawers/DrawerUserDetails'
import {DrawerWithdrawHistory} from '../partials/widgets/_new/drawers/DrawerWithdrawHistory'
import {DrawerTransactionHistory} from '../partials/widgets/_new/drawers/DrawerTransactionHistory'
import {DrawerCryptoTransactionDetails} from '../partials/widgets/_new/drawers/DrawerCryptoTransactionDetails'

/** Path for Role Management page — must match PrivateRoutes `path='role-management'` */
const ROLE_MANAGEMENT_PATH = '/role-management'

const normalizePathname = (pathname: string) => {
  if (!pathname) return '/'
  const p = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname
  return p || '/'
}

const isRoleManagementRoute = (pathname: string) => {
  const p = normalizePathname(pathname)
  return p === ROLE_MANAGEMENT_PATH || p.endsWith('/role-management')
}

const MasterLayout = () => {
  const location = useLocation()
  const {setRightTolbar} = useLayout()
  const [permissionErrorMsg, setPermissionErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    setPermissionErrorMsg(null)
  }, [location.pathname])

  useEffect(() => {
    const handlePermissionError = (e: Event) => {
      const customEvent = e as CustomEvent
      setPermissionErrorMsg(customEvent.detail?.message || 'You do not have permission to perform this action.')
    }
    
    window.addEventListener('api-permission-error', handlePermissionError)
    return () => {
      window.removeEventListener('api-permission-error', handlePermissionError)
    }
  }, [])

  useEffect(() => {
    reInitMenu()
  }, [location.key])

  /**
   * Global right toolbar state persists across navigations. Role Management sets
   * `rolesmanagement` (Add Role). Pages that don't set their own toolbar leave it stale,
   * so "Add Role" can appear on wrong screens until refresh. Clear only that key when
   * leaving the role-management route (other toolbars: dashboard, usermanagement, etc. stay).
   */
  useLayoutEffect(() => {
    const p = location.pathname || '/'
    const normalized = p.endsWith('/') && p.length > 1 ? p.slice(0, -1) : p
    const isRoleManagementRoute = normalized === '/role-management' || normalized.endsWith('/role-management')
    if (!isRoleManagementRoute) {
      setRightTolbar((prev: string | unknown) => (prev === 'rolesmanagement' ? '' : prev))
    }
  }, [location.pathname, setRightTolbar])

  return (
    <PageDataProvider>
      <div className='d-flex flex-column flex-root app-root' id='kt_app_root'>
        <div className='app-page flex-column flex-column-fluid' id='kt_app_page'>
          <HeaderWrapper />
          <div className='app-wrapper flex-column flex-row-fluid' id='kt_app_wrapper'>
            <Sidebar />
            <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
              <div className='d-flex flex-column flex-column-fluid' style={{ position: 'relative' }}>
                <ToolbarWrapper />
                <Content>
                  {permissionErrorMsg ? (
                    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '400px' }}>
                      {/* Blurred original content */}
                      <div style={{ filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' }}>
                        <Outlet />
                      </div>
                      
                      {/* Access Denied Overlay */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                      }}>
                        <div 
                          className='card border border-gray-200 shadow-sm' 
                          style={{ 
                            borderRadius: '12px', 
                            minHeight: '320px',
                            width: '100%',
                            maxWidth: '550px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                          }}
                        >
                          {/* Blurred decorative background elements */}
                          <div style={{
                            position: 'absolute',
                            width: '120px',
                            height: '120px',
                            borderRadius: '50%',
                            background: 'rgba(239, 68, 68, 0.08)',
                            filter: 'blur(30px)',
                            top: '20%',
                            left: '25%',
                          }}></div>
                          <div style={{
                            position: 'absolute',
                            width: '160px',
                            height: '160px',
                            borderRadius: '50%',
                            background: 'rgba(27, 132, 255, 0.04)',
                            filter: 'blur(45px)',
                            bottom: '20%',
                            right: '20%',
                          }}></div>

                          <div className='card-body d-flex flex-column align-items-center justify-content-center text-center p-8' style={{ zIndex: 1 }}>
                            <div 
                              className='mb-5 d-flex align-items-center justify-content-center'
                              style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                color: '#ef4444'
                              }}
                            >
                              <i className='bi bi-shield-slash-fill' style={{ fontSize: '28px' }}></i>
                            </div>
                            
                            <h2 className='fw-bold text-gray-900 mb-2 fs-2'>Access Denied</h2>
                            <p className='text-gray-500 fw-semibold fs-6 mb-0' style={{ maxWidth: '380px' }}>
                              {permissionErrorMsg}
                            </p>
                          </div>

                          {/* Tiny almost-invisible footer message */}
                          <div 
                            style={{
                              position: 'absolute',
                              bottom: '10px',
                              width: '100%',
                              textAlign: 'center',
                              fontSize: '8px',
                              color: 'rgba(156, 163, 175, 0.15)',
                              userSelect: 'none',
                              pointerEvents: 'none',
                              fontWeight: '500'
                            }}
                          >
                            baraj pgl
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Outlet />
                  )}
                </Content>
              </div>
              <FooterWrapper />
            </div>
          </div>
        </div>
      </div>

      {/* begin:: Drawers */}
      {/* <ActivityDrawer /> */}
      {/* <RightToolbar /> */}
      <DrawerMessenger />
      <DrawerTicketDetail />
      <SideModalIPList />
      <DrawerCryptoTransactionDetails />
      <DrawerUserDetails />
      <DrawerWithdrawHistory />
      <DrawerTransactionHistory />
      {/* end:: Drawers */}

      {/* begin:: Modals */}
      <InviteUsers />
      <UpgradePlan />
      {/* end:: Modals */}
      <ScrollTop />
    </PageDataProvider>
  )
}

export {MasterLayout}
